import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import EditIcon from '@mui/icons-material/Edit';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import PageContainer from '../layout/PageContainer';
import { FormControlLabel, Switch } from '@mui/material';
import { GET_OVERHEAD_COST_LINE_ITEMS_QUERY } from '../../queries/OverheadCostLineItemQueries';
import { OverheadCostLineItem } from '../../models/OverheadCostLineItem';
import dayjs from 'dayjs';
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  gridClasses
} from '@mui/x-data-grid';
import { toUSCurrency } from '../../utilities/CurrencyUtilities';

const INITIAL_PAGE_SIZE = 100;

export default function OverheadCostShow() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [includeInactiveCostItems, setIncludeInactiveCostItems] = useState(false);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: searchParams.get('page') ? Number(searchParams.get('page')) : 0,
    pageSize: searchParams.get('pageSize')
      ? Number(searchParams.get('pageSize'))
      : INITIAL_PAGE_SIZE,
  });

  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    searchParams.get('filter')
      ? JSON.parse(searchParams.get('filter') ?? '')
      : { items: [] },
  );

  const [sortModel, setSortModel] = useState<GridSortModel>(
    searchParams.get('sort') ? JSON.parse(searchParams.get('sort') ?? '') : [],
  );

  const [rowsState, setRowsState] = useState<{
    rows: OverheadCostLineItem[];
    rowCount: number;
  }>({
    rows: [],
    rowCount: 0,
  });

  const { loading, error, data } = useQuery(
    GET_OVERHEAD_COST_LINE_ITEMS_QUERY, {
    variables: {
      filter: includeInactiveCostItems ? {} : { isActive: { eq: true } }
    },
    fetchPolicy: 'cache-and-network'
  });

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);

      searchParams.set('page', String(model.page));
      searchParams.set('pageSize', String(model.pageSize));

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      setFilterModel(model);

      if (
        model.items.length > 0 ||
        (model.quickFilterValues && model.quickFilterValues.length > 0)
      ) {
        searchParams.set('filter', JSON.stringify(model));
      } else {
        searchParams.delete('filter');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model);

      if (model.length > 0) {
        searchParams.set('sort', JSON.stringify(model));
      } else {
        searchParams.delete('sort');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const loadData = useCallback(async () => {
    setRowsState({
      rows: data?.overheadCostLineItems || [],
      rowCount: data?.overheadCostLineItems.length || 0,
    });
  }, [data?.overheadCostLineItems]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCheckboxFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setIncludeInactiveCostItems(event.target.checked);

      loadData();
    }, [loadData]);

  const handleEdit = useCallback(() => {
    navigate(`/dashboard/overhead-costs/edit`);
  }, [navigate]);

  const initialState = useMemo(
    () => ({
      pagination: { paginationModel: { pageSize: INITIAL_PAGE_SIZE } },
    }),
    [],
  );

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'description',
        headerName: 'Description',
        width: 200
      },
      {
        field: 'totalCost',
        headerName: 'Total Cost ($)',
        type: 'number',
        width: 150,
        valueFormatter: (value: number) => toUSCurrency(value)
      },
      {
        field: 'totalAnnualCost',
        headerName: 'Total Annual Cost ($)',
        type: 'number',
        width: 150,
        valueFormatter: (value: number) => toUSCurrency(value)
      },
      {
        field: 'recipient',
        headerName: 'Recipient',
        width: 150,
        valueGetter: (_, row) => row.recipient ?? 'N/A'
      },
      {
        field: 'dateTimePurchased',
        headerName: 'Purchased',
        width: 150,
        valueFormatter: (value) => value ? dayjs(value).format('M/D/YYYY') : 'N/A'
      },
      {
        field: 'dateTimeDue',
        headerName: 'Due',
        width: 150,
        valueFormatter: (value) => value ? dayjs(value).format('M/D/YYYY') : 'N/A'
      },
      {
        field: 'dateTimePaid',
        headerName: 'Paid',
        width: 150,
        valueFormatter: (value) => value ? dayjs(value).format('M/D/YYYY') : 'N/A'
      },
      {
        field: 'notes',
        headerName: 'Notes',
        width: 200
      }
    ],
    [],
  );

  const renderShow = useMemo(() => {
    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return <>
      <Stack
        direction="row"
        spacing={2}
        sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box></Box>
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={includeInactiveCostItems}
                onChange={handleCheckboxFieldChange}
                size="small"
                slotProps={{
                  input: { 'aria-label': 'controlled' },
                }} />}
            label="Include Inactive Cost Items" />
        </Box>
      </Stack>
      <Box sx={{ height: '58vh', width: '100%' }}>
        <DataGrid
          density="compact"
          rows={rowsState.rows}
          columns={columns}
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          disableRowSelectionOnClick
          loading={loading}
          initialState={initialState}
          showToolbar
          pageSizeOptions={[INITIAL_PAGE_SIZE]}
          sx={{
            [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
              outline: 'transparent',
            },
            [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
            {
              outline: 'none',
            },
            [`& .${gridClasses.row}:hover`]: {
              cursor: 'pointer',
            }
          }}
          slotProps={{
            loadingOverlay: {
              variant: 'circular-progress',
              noRowsVariant: 'circular-progress',
            },
            baseIconButton: {
              size: 'small',
            },
          }}
        />
      </Box>
      <Divider sx={{ my: 3 }} />
      <Stack direction="row" spacing={2} justifyContent="space-between">
        <Stack direction="row" spacing={4} justifyContent="space-between">
          <Box>
            Total Costs: <strong>${toUSCurrency(data?.overheadCostLineItems?.map(item => item.totalCost || 0)?.reduce((a, b) => a + b, 0) || 0)}</strong>
          </Box>
          <Box>
            Total Annual Costs: <strong>${toUSCurrency(data?.overheadCostLineItems?.map(item => item.totalAnnualCost || 0)?.reduce((a, b) => a + b, 0) || 0)}</strong>
          </Box>
        </Stack>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={handleEdit}
        >
          Edit
        </Button>
      </Stack>
    </>;
  }, [columns, data?.overheadCostLineItems, error, filterModel, handleCheckboxFieldChange, handleEdit, handleFilterModelChange, handlePaginationModelChange, handleSortModelChange, includeInactiveCostItems, initialState, loading, paginationModel, rowsState.rows, sortModel]);

  return (
    <PageContainer
      title={'Overhead Costs'}
    >
      <Box sx={{ flex: 1, width: '100%' }}>{renderShow}</Box>
    </PageContainer>
  );
}
